import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';
import { MarketingHeroPill } from '@/components/marketing/v2/MarketingHeroPill';

function PosMockup() {
  return (
    <div className="relative">
      {/* Floating offline badge */}
      <div className="absolute -top-4 right-4 z-10 inline-flex items-center gap-2 rotate-[-3deg] border-2 border-charcoal-800 bg-lime-500 px-3 py-1.5 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.1em] text-charcoal-800 shadow-neobrutal-4">
        <span className="h-2 w-2 rounded-full border border-charcoal-800 bg-charcoal-800 animate-[pulse_2s_ease-in-out_infinite]" />
        Hors ligne — tu vends quand même
      </div>

      <div className="relative border-2 border-charcoal-800 bg-white shadow-neobrutal-14">
        <div className="flex">
          {/* Sidebar */}
          <aside className="flex w-14 flex-col items-center gap-4 border-r-2 border-charcoal-800 bg-charcoal-800 py-5">
            {['pos', 'stock', 'sales', 'team', 'ai'].map((k, i) => (
              <div
                key={k}
                className={`flex h-9 w-9 items-center justify-center border-2 border-charcoal-700 ${
                  i === 0 ? 'bg-lime-500 text-charcoal-800' : 'bg-charcoal-700 text-charcoal-400'
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="2" y="3" width="12" height="10" />
                  <path d="M2 7h12" />
                </svg>
              </div>
            ))}
          </aside>

          {/* Content */}
          <div className="flex-1 p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-marketing-mono text-[10px] font-medium uppercase tracking-[0.14em] text-charcoal-500">
                Caisse · Boutique Sira
              </div>
              <div className="inline-flex items-center gap-1.5 font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-lime-600">
                <span className="h-1.5 w-1.5 rounded-full bg-lime-500" />
                Session ouverte
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                { name: 'Coca 33cl', price: '500', tone: 'bg-paper' },
                { name: 'Baguette', price: '250', tone: 'bg-white' },
                { name: 'Yaourt', price: '450', tone: 'bg-paper' },
                { name: 'Pain choco', price: '300', tone: 'bg-white' },
                { name: 'Eau 1,5L', price: '400', tone: 'bg-paper' },
                { name: 'Chips', price: '650', tone: 'bg-white' },
              ].map((p) => (
                <div
                  key={p.name}
                  className={`border-2 border-charcoal-800 ${p.tone} p-2.5 shadow-neobrutal-2`}
                >
                  <div className="font-marketing-display text-[11px] font-semibold text-charcoal-800">
                    {p.name}
                  </div>
                  <div className="mt-1 font-marketing-mono text-[10px] text-charcoal-600">
                    {p.price} F
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ticket */}
        <div className="border-t-2 border-charcoal-800 bg-paper p-4">
          <div className="font-marketing-mono text-[10px] font-medium uppercase tracking-[0.14em] text-charcoal-600">
            Ticket en cours · 3 articles
          </div>
          <ul className="mt-2 space-y-1 font-marketing-body text-[13px]">
            <li className="flex justify-between border-b border-dashed border-charcoal-300 pb-1">
              <span>Coca 33 cl × 2</span>
              <span className="font-semibold">1 000 F</span>
            </li>
            <li className="flex justify-between border-b border-dashed border-charcoal-300 pb-1">
              <span>Baguette × 3</span>
              <span className="font-semibold">750 F</span>
            </li>
            <li className="flex justify-between border-b border-dashed border-charcoal-300 pb-1">
              <span>Yaourt × 1</span>
              <span className="font-semibold">450 F</span>
            </li>
          </ul>
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="font-marketing-mono text-[10px] uppercase tracking-[0.12em] text-charcoal-600">
                Total
              </div>
              <div className="font-marketing-display text-[40px] font-bold leading-none text-charcoal-800">
                8 200 <span className="text-lg font-semibold text-charcoal-600">F</span>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 border-2 border-charcoal-800 bg-lime-500 px-4 py-2.5 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-neobrutal-3"
            >
              Encaisser · Mobile Money
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M3 8h10" />
                <path d="M9 4l4 4-4 4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Decorative sticker */}
      <div className="absolute -bottom-4 -left-3 rotate-[-4deg] border-2 border-charcoal-800 bg-terracotta-500 px-3 py-1 font-marketing-mono text-[11px] font-bold uppercase tracking-[0.1em] text-white shadow-neobrutal-3">
        +32% de CA
      </div>
    </div>
  );
}

export default function HomeHeroV2() {
  const { t } = useTranslation('marketing');

  return (
    <section className="relative bg-paper pb-24 pt-16 md:pb-32 md:pt-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid items-center gap-14 md:grid-cols-[1.25fr_1fr] md:gap-16">
          <div>
            <MarketingHeroPill>
              {t('home.v2.eyebrow', 'POS nouvelle génération · marché africain')}
            </MarketingHeroPill>

            <h1 className="mt-7 font-marketing-display text-[clamp(48px,7vw,104px)] font-bold leading-[0.92] tracking-[-0.045em] text-charcoal-800">
              {t('home.v2.heroLine1', 'Ta caisse.')}
              <br />
              {t('home.v2.heroLine2', 'Rapide.')}{' '}
              <span className="underline-lime">{t('home.v2.heroHighlight', 'Augmentée')}</span>.
            </h1>

            <p className="mt-7 max-w-xl font-marketing-body text-lg leading-[1.5] text-charcoal-600 md:text-xl">
              {t(
                'home.v2.heroSubtitle',
                'Vends même hors ligne. Encaisse Orange, MTN, Wave. Pilote tes boutiques depuis un seul écran. Rejoins 5 200+ commerçants africains qui vendent mieux.',
              )}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <NeobrutalButton as="link" to="/register" variant="primary" size="lg">
                {t('home.v2.primaryCta', 'Commencer gratuit · 3 min')}
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M5 12h14" />
                  <path d="M13 6l6 6-6 6" />
                </svg>
              </NeobrutalButton>
              <NeobrutalButton as="link" to="/fonctionnalites" variant="secondary" size="lg">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
                {t('home.v2.secondaryCta', 'Voir la démo · 2 min')}
              </NeobrutalButton>
            </div>

            <div className="mt-10 flex items-center gap-4 font-marketing-body text-sm text-charcoal-600">
              <div className="flex -space-x-2">
                <span className="h-9 w-9 rounded-full border-2 border-white bg-lime-500" />
                <span className="h-9 w-9 rounded-full border-2 border-white bg-terracotta-500" />
                <span className="h-9 w-9 rounded-full border-2 border-white bg-charcoal-700" />
                <span className="h-9 w-9 rounded-full border-2 border-white bg-charcoal-400" />
              </div>
              <div>
                <Link to="/a-propos" className="font-semibold text-charcoal-800">
                  5 200+ commerçants
                </Link>{' '}
                <span className="text-charcoal-500">·</span>{' '}
                <span className="font-marketing-mono text-[12px] font-medium uppercase tracking-[0.1em]">
                  ★ 4.8/5 sur 1 240 avis
                </span>
              </div>
            </div>
          </div>

          <div className="md:order-last">
            <PosMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
